import { useId, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";

/** What a select holds once something is chosen: enough to send, and to show. */
export type Picked = { id: number; label: string };

export type SearchOption = {
  id: number;
  label: string;
  /** The second line: who it belongs to, where it is. */
  detail?: string;
};

/**
 * A select you type into, for the call bar: what is typed narrows the list
 * under it as it is typed, arrows move through it, Enter picks.
 *
 * Controlled on the text as well as the value, because what to list for a
 * given text is the host's to work out — a server search for companies, a
 * filter of a list already loaded for contacts — and a hook cannot be called
 * from inside a callback handed down here.
 *
 * Drawn for the bar's dark ground, with the list in flow rather than floating:
 * the bar sits at the edge of the screen and in the event modal's footer, and
 * a floating list would open past either edge.
 */
export function SearchSelect({
  label,
  placeholder,
  value,
  text,
  onText,
  onPick,
  options,
  status,
  footer,
  disabled = false,
}: {
  label: string;
  placeholder: string;
  value: Picked | null;
  text: string;
  onText: (text: string) => void;
  /** `null` when what is typed no longer matches what was chosen. */
  onPick: (picked: Picked | null) => void;
  options: SearchOption[];
  /** Searching, nothing found, failed — said in place of the list. */
  status?: string | null;
  /** Under the list: how many more there are than are shown. */
  footer?: ReactNode;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const inputId = useId();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const expanded = open && value === null;
  const activeOption = options[active];

  function pick(option: SearchOption) {
    onText(option.label);
    setOpen(false);
    onPick({ id: option.id, label: option.label });
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    // Never "save": the bar is a form, and Enter in a field submits it.
    if (event.key === "Enter") {
      event.preventDefault();
      if (expanded && activeOption) pick(activeOption);
      return;
    }
    if (!expanded) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (options.length === 0) return;
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((index) => (index + step + options.length) % options.length);
    } else if (event.key === "Escape") {
      // Close the list, not the event modal the bar may be inside.
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    }
  }

  return (
    <div className="min-w-[14rem] flex-1">
      <label htmlFor={inputId} className="mb-1 block text-xs text-ground/70">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          id={inputId}
          type="text"
          role="combobox"
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={expanded}
          {...(expanded ? { "aria-controls": listId } : {})}
          {...(expanded && activeOption
            ? { "aria-activedescendant": `${listId}-${activeOption.id}` }
            : {})}
          disabled={disabled}
          value={text}
          placeholder={placeholder}
          onChange={(event) => {
            onText(event.target.value);
            setActive(0);
            setOpen(true);
            // Typing over a choice un-chooses it: what the box says and what
            // gets saved must never disagree.
            if (value) onPick(null);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={onKeyDown}
          className="min-w-0 flex-1 border-2 border-ground/40 bg-transparent px-2 py-1.5 text-ground placeholder:text-ground/50 disabled:opacity-50"
        />
        {value ? (
          <button
            type="button"
            aria-label={t("call.prospect.clearLabel", { field: label })}
            onClick={() => {
              onText("");
              onPick(null);
            }}
            className="border-2 border-ground/40 px-2 py-1.5 text-xs text-ground hover:bg-ground/15"
          >
            {t("call.prospect.clear")}
          </button>
        ) : null}
      </div>

      {expanded && (status || options.length > 0) ? (
        <div className="mt-1 border-2 border-ground/40 bg-ground text-ink">
          {status ? (
            <p className="px-3 py-2 text-xs text-mute-600" aria-live="polite">
              {status}
            </p>
          ) : null}
          {options.length > 0 ? (
            <ul
              id={listId}
              role="listbox"
              aria-label={label}
              className="max-h-60 overflow-y-auto"
            >
              {options.map((option, index) => (
                <li
                  key={option.id}
                  id={`${listId}-${option.id}`}
                  role="option"
                  aria-selected={index === active}
                  className={`cursor-pointer border-b border-rule px-3 py-2 last:border-0 ${
                    index === active ? "bg-panel" : ""
                  }`}
                  // Keep the focus in the box, or its blur closes the list
                  // before the click can land on the option.
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => pick(option)}
                >
                  <p className="text-sm font-medium">{option.label}</p>
                  {option.detail ? (
                    <p className="text-xs text-mute-600">{option.detail}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
          {footer ? (
            <p className="border-t border-rule px-3 py-1.5 text-[11px] text-mute-600">
              {footer}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Lower case, accents off, so "Trevino" finds "Treviño". */
export function folded(text: string): string {
  return text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}
